/**
 * Typed JSON-LD builders (LL-0015 Wave 2, Lane S).
 *
 * One module for every structured-data emitter on the site, typed with
 * schema-dts so an invalid shape is a compile error, not a Rich Results
 * surprise. Consumers:
 *   - lib/blog.ts articleJsonLd()  -> articleLd() here
 *   - scripts/prerender.mjs        -> breadcrumbLdForPath() via entry-server
 *   - (GATED, do not wire) eventLd() / eventListLd(): built and tested, but
 *     emission on any page waits on the founder /events re-index decision
 *     (gate G1 in the LL-0015 program). Do not import them into a page.
 *
 * Serialization rule: always emit through serializeLd(), which escapes `<`
 * so producer-supplied text can never break out of an inline
 * <script type="application/ld+json"> tag.
 */
import type {
  Article,
  BreadcrumbList,
  Event as SchemaEvent,
  FAQPage,
  ItemList,
  WithContext,
} from "schema-dts";
import type { BlogPost } from "./blog";

const SITE = "https://www.letsloop.app";

/** JSON.stringify hardened for inline <script> embedding. */
export function serializeLd(ld: object): string {
  return JSON.stringify(ld).replace(/</g, "\\u003c");
}

// ─── Article ───────────────────────────────────────────────────────────

export function articleLd(post: BlogPost): WithContext<Article> {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.description,
    keywords: post.keywords.join(", "),
    datePublished: post.date || undefined,
    // Bumped only on real content edits (see route-lastmod.mjs rules).
    dateModified: post.dateModified || post.date || undefined,
    // Local heroes are site-relative; a Sanity CDN hero is already an absolute URL. Anything else
    // (a hand-typed relative path) falls back to the site card rather than emitting a broken URL.
    image: post.image?.startsWith("/")
      ? `${SITE}${post.image}`
      : post.image?.startsWith("https://")
        ? post.image
        : `${SITE}/og/home.jpg`,
    // Organization author until the Person-byline gate (G10) opens: never
    // invent a human author.
    author: { "@type": "Organization", name: "LetsLoop", url: `${SITE}/` },
    publisher: {
      "@type": "Organization",
      name: "LetsLoop",
      logo: { "@type": "ImageObject", url: `${SITE}/letsloop-logo.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": post.canonical },
  };
}

// ─── BreadcrumbList ────────────────────────────────────────────────────

const SECTION_NAMES: Record<string, string> = {
  news: "News",
  events: "Events",
  people: "People",
};

/**
 * Breadcrumb for a route path. `pageName` names the leaf (the page title
 * without the " | LetsLoop" suffix); intermediate segments use the section
 * vocabulary above, falling back to the raw segment.
 */
export function breadcrumbLd(
  path: string,
  pageName: string,
): WithContext<BreadcrumbList> {
  const segments = path.split("/").filter(Boolean);
  const items = [{ name: "Home", item: `${SITE}/` }];
  let acc = "";
  segments.forEach((segment, i) => {
    acc += `/${segment}`;
    items.push({
      name:
        i === segments.length - 1
          ? pageName
          : (SECTION_NAMES[segment] ?? segment),
      item: `${SITE}${acc}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

/** Serialized breadcrumb, ready to inline. Used by scripts/prerender.mjs. */
export function breadcrumbLdForPath(path: string, pageName: string): string {
  return serializeLd(breadcrumbLd(path, pageName));
}

// ─── FAQPage ───────────────────────────────────────────────────────────

/**
 * FAQPage for a page's OWN visible FAQ (never shared across pages: the
 * site-wide duplicate FAQPage was exactly the defect Lane S removed).
 * Answers must be plain-text mirrors of the rendered copy: Google dropped
 * FAQ rich results in 2026, but LLMs still parse the markup, and schema
 * for content the page does not visibly show is a spam signal.
 */
export function faqLd(
  faqs: Array<{ q: string; aText: string }>,
): WithContext<FAQPage> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, aText }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: aText },
    })),
  };
}

// ─── Event + ItemList (BUILT, NOT WIRED — gate G1) ─────────────────────

/**
 * The only event shape eligible for Event markup. Guardrails are the point:
 * Google's event policy makes invite-only or membership-gated happenings
 * INELIGIBLE (marking them up is a structured-data spam risk), and the
 * public site only ever renders hand-authored grassroots events.
 */
export interface PublicLoopEvent {
  slug: string;
  title: string;
  description?: string | null;
  /** ISO-8601 with timezone, e.g. "2026-08-10T18:30:00+01:00". */
  startDate: string;
  endDate?: string | null;
  venueName: string;
  addressLocality?: string;
  postalCode?: string | null;
  imageUrl?: string | null;
  /** Must be "grassroots" — the public-surface provenance filter. */
  provenance: string;
  /** True only when anyone can turn up without an invite or app account. */
  openToAnyone: boolean;
}

export function eventLd(ev: PublicLoopEvent): WithContext<SchemaEvent> {
  if (ev.provenance !== "grassroots") {
    throw new Error(
      `eventLd: refusing non-grassroots event "${ev.slug}" (public surface renders grassroots only)`,
    );
  }
  if (!ev.openToAnyone) {
    throw new Error(
      `eventLd: refusing invite-only/app-gated event "${ev.slug}" (ineligible for Event markup, spam risk)`,
    );
  }
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: ev.title,
    description: ev.description || undefined,
    startDate: ev.startDate,
    endDate: ev.endDate || undefined,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: ev.venueName,
      address: {
        "@type": "PostalAddress",
        addressLocality: ev.addressLocality ?? "London",
        postalCode: ev.postalCode || undefined,
        addressCountry: "GB",
      },
    },
    image: ev.imageUrl || undefined,
    isAccessibleForFree: true,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      url: `${SITE}/events/${ev.slug}`,
    },
    organizer: {
      "@type": "Organization",
      name: "LetsLoop",
      url: `${SITE}/`,
    },
    url: `${SITE}/events/${ev.slug}`,
  };
}

/**
 * ItemList of event LEAF URLS for a hub page. Google only surfaces events
 * as rich results from their own unique leaf pages, so the hub lists
 * pointers, never inline Event markup.
 */
export function eventListLd(
  events: PublicLoopEvent[],
  listName: string,
): WithContext<ItemList> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    itemListElement: events.map((ev, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/events/${ev.slug}`,
    })),
  };
}
